import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Divider,
  Alert,
  Paper,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataService } from 'src/services/data-service';
import SecurityDepositDialog from './SecurityDepositDialog';

interface Fee {
  feeTypeId: string;
  currencyId: string;
  value: number;
  billingCycle: string;
  notes: string;
}

interface LeaseCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Tenant {
  id: string;
  fullName: string;
  emails: string[];
  phoneNumbers: string[];
}

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  areaM2: number;
}

interface LeaseType {
  id: string;
  name: string;
  description: string;
}

interface Currency {
  id: string;
  code: string;
  name: string;
}

interface FeeType {
  id: string;
  name: string;
  description: string;
}

interface UnitFeeConfig {
  id: string;
  daily: number;
  weekly: number;
  monthly: number;
  bimonthly: number;
  quarterly: number;
  semiAnnual: number;
  annual: number;
  oneTime: number;
  feeType: string;
  active: boolean;
}

const BILLING_CYCLES = [
  'Daily',
  'Weekly',
  'Monthly',
  'Bimonthly',
  'Quarterly',
  'SemiAnnual',
  'Annual',
  'OneTime'
];

const BILLING_CYCLE_MAP: { [key: string]: keyof UnitFeeConfig } = {
  'Daily': 'daily',
  'Weekly': 'weekly',
  'Monthly': 'monthly',
  'Bimonthly': 'bimonthly',
  'Quarterly': 'quarterly',
  'SemiAnnual': 'semiAnnual',
  'Annual': 'annual',
  'OneTime': 'oneTime'
};

const LeaseCreateDialog: React.FC<LeaseCreateDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Security deposit dialog state
  const [showSecurityDepositDialog, setShowSecurityDepositDialog] = useState(false);
  const [createdLeaseId, setCreatedLeaseId] = useState<string | null>(null);

  // Dropdown data
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [leaseTypes, setLeaseTypes] = useState<LeaseType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [unitFeeConfigs, setUnitFeeConfigs] = useState<UnitFeeConfig[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    unitId: '',
    tenantId: '',
    leaseTypeId: '',
    currencyId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    paymentDateOn: 1,
    mainFeeTypeId: ''
  });

  const [fees, setFees] = useState<Fee[]>([
    {
      feeTypeId: '',
      currencyId: '',
      value: 0,
      billingCycle: 'Monthly',
      notes: ''
    }
  ]);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
    }
  }, [open]);

  const fetchDropdownData = async () => {
    try {
      setLoading(true);

      // Fetch all required data in parallel
      const [tenantsRes, unitsRes, leaseTypesRes, currenciesRes, feeTypesRes] = await Promise.all([
        DataService.get('api/Tenant/all'),
        DataService.get('api/Unit/active'),
        DataService.get('api/lease-types/all'),
        DataService.get('api/Currency/all'),
        DataService.get('api/fee-types/all')
      ]);

      // Process tenants
      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        console.log('Tenants data:', data);
        setTenants(data.success ? data.data : (Array.isArray(data) ? data : []));
      }

      // Process units
      if (unitsRes.ok) {
        const data = await unitsRes.json();
        console.log('Units data:', data);
        setUnits(data.success ? data.data : (Array.isArray(data) ? data : []));
      }

      // Process lease types
      if (leaseTypesRes.ok) {
        const data = await leaseTypesRes.json();
        console.log('Lease Types data:', data);
        const leaseTypesArray = data.success ? data.data : (Array.isArray(data) ? data : []);
        setLeaseTypes(leaseTypesArray);
        console.log('Lease Types set to:', leaseTypesArray);
      } else {
        console.error('Lease Types response not ok:', leaseTypesRes.status);
      }

      // Process currencies
      if (currenciesRes.ok) {
        const data = await currenciesRes.json();
        console.log('Currencies data:', data);
        setCurrencies(data.success ? data.data : (Array.isArray(data) ? data : []));
      }

      // Process fee types
      if (feeTypesRes.ok) {
        const data = await feeTypesRes.json();
        console.log('Fee Types data:', data);
        const feeTypesArray = data.success ? data.data : (Array.isArray(data) ? data : []);
        setFeeTypes(feeTypesArray);
        console.log('Fee Types set to:', feeTypesArray);
      } else {
        console.error('Fee Types response not ok:', feeTypesRes.status);
      }

    } catch (err) {
      console.error('Error fetching dropdown data:', err);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnitFeeConfigs = async (unitId: string) => {
    try {
      const response = await DataService.get(`api/UnitFeeConfig/unit/${unitId}`);

      if (response.ok) {
        const data = await response.json();
        console.log('Unit Fee Configs:', data);

        const configs: UnitFeeConfig[] = data.success ? data.data : (Array.isArray(data) ? data : []);

        // Store the configs for later reference
        setUnitFeeConfigs(configs);

        if (configs.length > 0) {
          // Convert unit fee configs to fee form entries
          const newFees: Fee[] = configs.map((config) => {
            // Find the fee type ID by matching the name
            const feeTypeMatch = feeTypes.find(ft => ft.name === config.feeType);

            // Determine which billing cycle has the highest value (most likely to be used)
            let bestCycle = 'Monthly';
            let bestValue = config.monthly;

            if (config.daily > 0 && config.daily > bestValue) { bestCycle = 'Daily'; bestValue = config.daily; }
            if (config.weekly > 0 && config.weekly > bestValue) { bestCycle = 'Weekly'; bestValue = config.weekly; }
            if (config.bimonthly > 0 && config.bimonthly > bestValue) { bestCycle = 'Bimonthly'; bestValue = config.bimonthly; }
            if (config.quarterly > 0 && config.quarterly > bestValue) { bestCycle = 'Quarterly'; bestValue = config.quarterly; }
            if (config.semiAnnual > 0 && config.semiAnnual > bestValue) { bestCycle = 'SemiAnnual'; bestValue = config.semiAnnual; }
            if (config.annual > 0 && config.annual > bestValue) { bestCycle = 'Annual'; bestValue = config.annual; }
            if (config.oneTime > 0 && config.oneTime > bestValue) { bestCycle = 'OneTime'; bestValue = config.oneTime; }

            return {
              feeTypeId: feeTypeMatch?.id || '',
              currencyId: formData.currencyId,
              value: bestValue,
              billingCycle: bestCycle,
              notes: `Pre-configured fee from unit`
            };
          });

          setFees(newFees);
          console.log('Populated fees:', newFees);
        }
      }
    } catch (err) {
      console.error('Error fetching unit fee configs:', err);
      // Don't show error to user, just log it
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeeChange = (index: number, field: keyof Fee, value: any) => {
    const updatedFees = [...fees];
    updatedFees[index] = {
      ...updatedFees[index],
      [field]: value
    };

    // If billing cycle or fee type changed, update the amount from unit fee config
    if ((field === 'billingCycle' || field === 'feeTypeId') && unitFeeConfigs.length > 0) {
      const currentFee = updatedFees[index];

      // Get the fee type ID - use the new value if feeTypeId is being changed
      const feeTypeId = field === 'feeTypeId' ? value : currentFee.feeTypeId;

      // Find the fee type name for the current fee
      const feeTypeObj = feeTypes.find(ft => ft.id === feeTypeId);

      if (feeTypeObj) {
        // Find the matching unit fee config
        const matchingConfig = unitFeeConfigs.find(config => config.feeType === feeTypeObj.name);

        if (matchingConfig) {
          // Get the billing cycle - use the new value if billingCycle is being changed
          const billingCycle = field === 'billingCycle' ? value : currentFee.billingCycle;

          // Get the amount for the selected billing cycle
          const cycleKey = BILLING_CYCLE_MAP[billingCycle as string];
          if (cycleKey) {
            const amount = matchingConfig[cycleKey] as number;
            updatedFees[index].value = amount;
            console.log(`Updated fee amount to ${amount} for ${feeTypeObj.name} - ${billingCycle} billing cycle`);
          }
        }
      }
    }

    setFees(updatedFees);
  };

  const addFee = () => {
    setFees([
      ...fees,
      {
        feeTypeId: '',
        currencyId: formData.currencyId,
        value: 0,
        billingCycle: 'Monthly',
        notes: ''
      }
    ]);
  };

  const removeFee = (index: number) => {
    if (fees.length > 1) {
      setFees(fees.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.unitId || !formData.tenantId || !formData.leaseTypeId ||
        !formData.currencyId || !formData.startDate || !formData.endDate ||
        !formData.mainFeeTypeId) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate fees
    const invalidFee = fees.find(
      (fee) => !fee.feeTypeId || !fee.currencyId || fee.value <= 0 || !fee.billingCycle
    );
    if (invalidFee) {
      setError('Please complete all fee information');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        createdDate: new Date().toISOString(),
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        fees: fees.map(fee => ({
          ...fee,
          value: Number(fee.value)
        }))
      };

      console.log('Submitting lease:', payload);

      const response = await DataService.post('api/Lease/create', payload);

      if (response.ok) {
        // Extract the lease ID from the response
        const responseData = await response.json();
        let leaseId: string | null = null;

        if (responseData.success && responseData.data && responseData.data.id) {
          leaseId = responseData.data.id;
        } else if (responseData.id) {
          leaseId = responseData.id;
        }

        console.log('Lease created with ID:', leaseId);

        // Close the create dialog
        handleClose();

        // Show security deposit dialog if we have a lease ID
        if (leaseId) {
          setCreatedLeaseId(leaseId);
          setShowSecurityDepositDialog(true);
        } else {
          // If no ID, just call onSuccess
          onSuccess();
        }
      } else {
        // Handle error response
        let errorMessage = `Failed to create lease (${response.status})`;
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          // Response might not be JSON (404, 500, etc.)
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
        console.error('Lease creation failed:', errorMessage, 'Response:', response);
      }
    } catch (err) {
      console.error('Error creating lease:', err);
      setError('An error occurred while creating the lease');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      unitId: '',
      tenantId: '',
      leaseTypeId: '',
      currencyId: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      paymentDateOn: 1,
      mainFeeTypeId: ''
    });
    setFees([
      {
        feeTypeId: '',
        currencyId: '',
        value: 0,
        billingCycle: 'Monthly',
        notes: ''
      }
    ]);
    setError(null);
    onClose();
  };

  const handleSecurityDepositSuccess = () => {
    onSuccess();
  };

  const handleSecurityDepositSkip = () => {
    onSuccess();
  };

  const selectedTenant = tenants.find((t) => t.id === formData.tenantId);
  const selectedUnit = units.find((u) => u.id === formData.unitId);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="bold">
            Create New Lease
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Tenant Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Tenant Information
            </Typography>
            <Autocomplete<Tenant, false, false, false>
              options={tenants}
              getOptionLabel={(option) => option.fullName}
              value={selectedTenant || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleInputChange('tenantId', newValue.id);
                } else {
                  handleInputChange('tenantId', '');
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Tenant" required />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.emails[0] || 'No email'} • {option.phoneNumbers[0] || 'No phone'}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          {/* Unit Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Unit Information
            </Typography>
            <Autocomplete<Unit, false, false, false>
              options={units}
              getOptionLabel={(option) => `Unit ${option.number}`}
              value={selectedUnit || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleInputChange('unitId', newValue.id);
                  // Fetch and populate fee configs for this unit
                  if (feeTypes.length > 0) {
                    fetchUnitFeeConfigs(newValue.id);
                  }
                } else {
                  handleInputChange('unitId', '');
                  // Reset fees to default
                  setFees([
                    {
                      feeTypeId: '',
                      currencyId: formData.currencyId,
                      value: 0,
                      billingCycle: 'Monthly',
                      notes: ''
                    }
                  ]);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Unit" required />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">Unit {option.number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.rooms} rooms • {option.bathrooms} baths • {option.areaM2} m²
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Lease Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Lease Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Lease Type</InputLabel>
              <Select
                value={formData.leaseTypeId}
                onChange={(e) => handleInputChange('leaseTypeId', e.target.value)}
                label="Lease Type"
              >
                {leaseTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currencyId}
                onChange={(e) => handleInputChange('currencyId', e.target.value)}
                label="Currency"
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code} - {currency.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Date (Day of Month)"
              type="number"
              value={formData.paymentDateOn}
              onChange={(e) => handleInputChange('paymentDateOn', parseInt(e.target.value))}
              inputProps={{ min: 1, max: 31 }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Main Fee Type</InputLabel>
              <Select
                value={formData.mainFeeTypeId}
                onChange={(e) => handleInputChange('mainFeeTypeId', e.target.value)}
                label="Main Fee Type"
              >
                {feeTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Fees Section */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle2" color="primary" fontWeight="600">
                Fees Configuration
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addFee}
                variant="outlined"
                size="small"
              >
                Add Fee
              </Button>
            </Box>
          </Grid>

          {fees.map((fee, index) => (
            <Grid item xs={12} key={index}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Chip label={`Fee ${index + 1}`} color="primary" size="small" />
                  {fees.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFee(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required size="small">
                      <InputLabel>Fee Type</InputLabel>
                      <Select
                        value={fee.feeTypeId}
                        onChange={(e) => handleFeeChange(index, 'feeTypeId', e.target.value)}
                        label="Fee Type"
                      >
                        {feeTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required size="small">
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={fee.currencyId}
                        onChange={(e) => handleFeeChange(index, 'currencyId', e.target.value)}
                        label="Currency"
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.id} value={currency.id}>
                            {currency.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={fee.value}
                      onChange={(e) => handleFeeChange(index, 'value', e.target.value)}
                      required
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required size="small">
                      <InputLabel>Billing Cycle</InputLabel>
                      <Select
                        value={fee.billingCycle}
                        onChange={(e) => handleFeeChange(index, 'billingCycle', e.target.value)}
                        label="Billing Cycle"
                      >
                        {BILLING_CYCLES.map((cycle) => (
                          <MenuItem key={cycle} value={cycle}>
                            {cycle}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={fee.notes}
                      onChange={(e) => handleFeeChange(index, 'notes', e.target.value)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Lease'}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Security Deposit Dialog */}
      {createdLeaseId && (
        <SecurityDepositDialog
          open={showSecurityDepositDialog}
          onClose={() => setShowSecurityDepositDialog(false)}
          onSuccess={handleSecurityDepositSuccess}
          onSkip={handleSecurityDepositSkip}
          leaseId={createdLeaseId}
        />
      )}
    </>
  );
};

export default LeaseCreateDialog;
